// =========================================================================
// Deposit addresses — step 2 of the operator's flow, the one tap (21 Oct 2026).
//
// "I will need them multiple times on the go, need easier solution and
// reliable and not complex at all but robust" (the operator, 18 Oct). So: one
// button, New address, no customer and no deal to type — the same statements
// the wallet bot's /new runs, so the two cannot disagree about the next
// index. The customer is attached when the money lands (the bot's Tag
// customer button) or here, with Tag — once. Each row carries its QR code so
// the operator can show a customer the address from here as well.
//
// On the design system (22 Oct 2026): the address is the figure; the lines
// under it are one each; the explanation is behind About.
// =========================================================================

import { SigninQuiet } from '../../components/v2/custody/SigninLine';
import { isCustodySignin } from '../../lib/errors';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QrCode, Copy, Plus } from 'lucide-react';
import { custody as api, type DepositAddress } from '../../lib/api';
import { ApiError } from '../../lib/errors';
import { fmtDate } from '../../lib/format';
import { QrSvg } from '../../components/CosignHandoff';
import { Card, StateLine, EmptyState, Pending, Value, MoreDetails } from '../../components/kit';
import { checkAddress, CHECK_WORDS, type AddressCheck } from '../../lib/custody-keys-local';
import { buildNetwork } from '../../lib/ledger-cable';
import { FIELD, SELECT } from './field';

/** The one line under an address: checked here, a mismatch in red, or unchecked. A mismatch also hides the QR code. */
function AddressCheckLine({ check }: { check: AddressCheck }) {
  return (
    <StateLine tone={check === 'mismatch' ? 'bad' : 'neutral'} className="mt-0.5">
      {CHECK_WORDS[check]}
    </StateLine>
  );
}
/** What this browser derives for an address, against the server's word. */
function checkOf(a: { address: string; derivation: string | null | undefined }): AddressCheck {
  const network = buildNetwork();
  return network ? checkAddress(network, { address: a.address, derivation: a.derivation, account: 0 }) : 'unchecked';
}

const textOf = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);

/** The refusals in the operator's words: one line, the server's reason behind it. */
function issueRefusal(err: unknown): { line: string; more: string } {
  const reason = err instanceof ApiError && typeof err.body.reason === 'string' ? err.body.reason : null;
  if (reason === 'no_key') return { line: 'No deposit key yet — register account 1 first.', more: 'Register your Ledger\'s account 1 in Devices & keys, then try again.' };
  return { line: "Couldn't issue an address — try again.", more: textOf(err) };
}
function tagRefusal(err: unknown): { line: string; more: string } {
  const reason = err instanceof ApiError && typeof err.body.reason === 'string' ? err.body.reason : null;
  switch (reason) {
    case 'already_tagged': return { line: 'Already tagged — a tag is given once.', more: textOf(err) };
    case 'party': return { line: 'No such customer in the Books.', more: textOf(err) };
    case 'deal': return { line: 'No such deal in the Books.', more: textOf(err) };
    case 'deal_not_theirs': return { line: 'That deal is another customer\'s.', more: textOf(err) };
    case 'deal_voided': return { line: 'That deal was voided.', more: textOf(err) };
    // The server's own guard: the address is not what its registered key puts at its path (27 Oct 2026).
    case 'address_mismatch': return { line: 'Address does not match its key. Not tagged.', more: textOf(err) };
    default: return { line: "Couldn't tag — try again.", more: textOf(err) };
  }
}

function CopyButton({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* no clipboard here; the address is on screen to read */ }
  }
  return (
    <button className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-tg-hint lg:min-h-[40px] lg:min-w-[40px]" aria-label="Copy address" title="Copy address" onClick={() => void copy()}>
      <Copy className="h-4 w-4" />{copied && <span className="ml-1 text-meta text-pos">Copied</span>}
    </button>
  );
}

/** The one tap: a fresh address, shown large with its QR code as soon as it exists. */
function NewAddress() {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const issue = useMutation({
    mutationFn: () => api.newDepositAddress(note.trim() || undefined),
    onSuccess: () => { setNote(''); void qc.invalidateQueries({ queryKey: ['custody', 'deposit-addresses'] }); },
  });
  const fresh = issue.data ?? null;
  const refusal = issue.isError ? issueRefusal(issue.error) : null;
  return (
    <Card>
      <div className="flex flex-wrap items-end gap-2">
        <label className="block min-w-0 flex-1">
          <span className="mb-1 block text-meta text-tg-hint">Note (optional)</span>
          <input className={`${FIELD} w-full`} value={note} maxLength={200} placeholder="ali 5k" onChange={(e) => setNote(e.target.value)} disabled={issue.isPending} />
        </label>
        <button className="btn-primary flex min-h-[44px] items-center gap-2 text-body" disabled={issue.isPending} onClick={() => issue.mutate()}>
          <Plus className="h-4 w-4" /> {issue.isPending ? 'Issuing…' : 'New address'}
        </button>
      </div>
      {refusal && <StateLine tone="bad" className="mt-2" more={refusal.more}>{refusal.line}</StateLine>}
      {fresh && (() => {
        const check = checkOf(fresh);
        return (
          <div className="mt-3">
            {check !== 'mismatch' && (
              <div className="mx-auto w-full max-w-[240px] rounded-xl bg-white p-2">
                <QrSvg text={fresh.address} className="h-auto w-full" label={`Deposit address QR code for ${fresh.address}`} />
              </div>
            )}
            <p className="mt-2 break-all text-center font-mono text-body">{fresh.address}</p>
            <p className="mt-1 text-center text-meta text-tg-hint">USDT · TRON · path {fresh.derivation} · {fresh.label} · untagged</p>
            <div className="text-center"><AddressCheckLine check={check} /></div>
            {check !== 'mismatch' && <div className="mt-1 flex justify-center"><CopyButton address={fresh.address} /></div>}
          </div>
        );
      })()}
    </Card>
  );
}

/** Tag — once — from the customer list; a deal is optional. */
function TagControl({ a, customers }: { a: DepositAddress; customers: Array<{ slug: string; name: string }> }) {
  const qc = useQueryClient();
  const [slug, setSlug] = useState('');
  const [dealId, setDealId] = useState('');
  const tag = useMutation({
    mutationFn: () => api.tagDepositAddress(a.id, { partySlug: slug, ...(dealId.trim() ? { dealId: dealId.trim() } : {}) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['custody', 'deposit-addresses'] });
      void qc.invalidateQueries({ queryKey: ['custody', 'deposits'] });
    },
  });
  const refusal = tag.isError ? tagRefusal(tag.error) : null;
  // A form: Enter in the deal field sends the tag like the button does (the operator's pass, 23 Oct 2026 — a tag he believed given never reached the desk).
  return (
    <form className="mt-2 flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); if (slug && !tag.isPending) tag.mutate(); }}>
      <label className="block min-w-0 flex-1">
        <span className="mb-1 block text-meta text-tg-hint">Tag customer</span>
        <select className={`${SELECT} w-full`} value={slug} onChange={(e) => setSlug(e.target.value)} disabled={tag.isPending}>
          <option value="">Choose…</option>
          {customers.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
      </label>
      <label className="block w-28">
        <span className="mb-1 block text-meta text-tg-hint">Deal (optional)</span>
        <input className={`${FIELD} w-full`} value={dealId} placeholder="D-12" onChange={(e) => setDealId(e.target.value)} disabled={tag.isPending} />
      </label>
      <button type="submit" className="btn-secondary min-h-[44px] text-body" disabled={!slug || tag.isPending}>{tag.isPending ? 'Tagging…' : 'Tag'}</button>
      {refusal && <StateLine tone="bad" className="w-full" more={refusal.more}>{refusal.line}</StateLine>}
    </form>
  );
}

function AddressCard({ a, customers }: { a: DepositAddress; customers: Array<{ slug: string; name: string }> }) {
  const [showQr, setShowQr] = useState(false);
  const notes = (a.notes ?? '').split('\n').map((l) => l.trim()).filter((l) => l && !/^\d{4}-\d{2}-\d{2}: issued /.test(l));
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Value strong>{a.partyName ?? a.partySlug ?? <span className="text-tg-hint">Untagged</span>}</Value>
          <div className="text-meta text-tg-hint">
            {a.dealId ? <>Deal {a.dealId}</> : 'No deal'}
            {a.label && <> · {a.label}</>}
            {a.issuedAt && <> · issued {fmtDate(a.issuedAt)}</>}
          </div>
        </div>
        {checkOf(a) !== 'mismatch' && (
          <div className="flex shrink-0 gap-1">
            <button className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-tg-hint lg:min-h-[40px] lg:min-w-[40px]" aria-label="Show QR code" title="Show QR code" onClick={() => setShowQr((v) => !v)}>
              <QrCode className="h-4 w-4" />
            </button>
            <CopyButton address={a.address} />
          </div>
        )}
      </div>
      <p className="mt-2 break-all font-mono text-body">{a.address}</p>
      <p className="mt-0.5 text-meta text-tg-hint">
        {a.derivation ? <>path {a.derivation}</> : 'not issued from a key'}
      </p>
      {/* Every row is checked, path or not: with a key kept here, a row the server names no path for is a mismatch (Astra, 22 Oct 2026). A hand row with no key here has nothing to say. */}
      {(a.derivation || checkOf(a) === 'mismatch') && <AddressCheckLine check={checkOf(a)} />}
      {notes.length > 0 && (
        <ul className="mt-1 text-meta text-tg-hint">
          {notes.map((n) => <li key={n}>{n}</li>)}
        </ul>
      )}
      {showQr && checkOf(a) !== 'mismatch' && (
        <div className="mx-auto mt-3 w-full max-w-[220px] rounded-xl bg-white p-2">
          <QrSvg text={a.address} className="h-auto w-full" label={`Deposit address QR code for ${a.partyName ?? a.address}`} />
        </div>
      )}
      {!a.partySlug && <TagControl a={a} customers={customers} />}
    </Card>
  );
}

export function Addresses() {
  const [customer, setCustomer] = useState('');
  const customers = useQuery({ queryKey: ['custody', 'customers'], queryFn: api.customers });
  const q = useQuery({
    queryKey: ['custody', 'deposit-addresses', customer],
    queryFn: () => api.depositAddresses(customer || undefined),
    refetchInterval: 30_000,
  });
  const list = customers.data?.customers ?? [];

  return (
    <div className="space-y-3">
      <NewAddress />

      <label className="block">
        <span className="mb-1 block text-meta text-tg-hint">Show</span>
        <select className={`${SELECT} w-full`} value={customer} onChange={(e) => setCustomer(e.target.value)}>
          <option value="">Every address</option>
          {list.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
      </label>

      {q.isError ? (
        isCustodySignin(q.error) ? <SigninQuiet error={q.error} /> : <StateLine tone="bad" more={textOf(q.error)}>Couldn't read the addresses — try again.</StateLine>
      ) : !q.data ? (
        <Pending what="Reading…" />
      ) : q.data.addresses.length === 0 ? (
        <Card><EmptyState>No deposit addresses yet.</EmptyState></Card>
      ) : (
        q.data.addresses.map((a) => <AddressCard key={a.id} a={a} customers={list} />)
      )}

      <MoreDetails label="About addresses">
        <p className="text-meta text-tg-hint">One tap, here or in the wallet bot: a fresh address from the registered account key, as a QR with the address under it. The customer is attached when the money lands — the bot's Tag customer button, or Tag here — once.</p>
      </MoreDetails>
    </div>
  );
}
